import { useEffect, useState } from "react";
import { Crown, Star, Shield, Users, TrendingUp, Award, Eye, Mail, Phone, MapPin, Building, DollarSign, Calendar, Clock, Target, CheckCircle } from "lucide-react";
import { updateUserProfile, saveExclusiveRequest, getUserProfile, getTechnologyNeeds, addTechnologyNeed, removeTechnologyNeed, hasExclusiveRequest } from "../../utils/api";

const ExclusiveBuyerForm = () => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [techstack, setTechstack] = useState('');
  const [businessChallenges, setBusinessChallenges] = useState('');
  const [technologyGoals, setTechnologyGoals] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [purchasingRole, setPurchasingRole] = useState(null);
  const [purchasingTimeline, setPurchasingTimeline] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [technicalNeeds, setTechnicalNeeds] = useState([]);
  const [selectedNeeds, setSelectedNeeds] = useState([]);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [isExclusive, setIsExclusive] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [biography, setBiography] = useState('');

  useEffect(() => {
    async function checkPendingRequest() {
      const res = await hasExclusiveRequest();
      if (res.success) {
        setHasPendingRequest(res.data);
      } else {
        console.error("Failed to check pending request:", res.message);
      }
    }

    async function fetchUserProfile() {
      const res = await getUserProfile();
      if (res.success) {
        setUserProfile(res.data);
        setTechstack(res.data.technology_stack || '');
        setBusinessChallenges(res.data.business_challenges || '');
        setTechnologyGoals(res.data.technology_goals || '');
        setLinkedinUrl(res.data.linkedin_url || '');
        setPurchasingRole(res.data.purchasing_role || null);
        setPurchasingTimeline(res.data.purchasing_timeline || null);
        setBiography(res.data.biography || '');
        setSelectedNeeds(res.data.technology_needs.map(x => x.technology_needs_id) || []);
      } else {
        console.error("Failed to fetch user profile:", res.message);
      }
    }

    async function fetchTechnicalNeeds() {
      const res = await getTechnologyNeeds();
      if (res.success) {
        setTechnicalNeeds(res.data);
      } else {
        console.error("Failed to fetch technical needs:", res.message);
      }
    }

    checkPendingRequest();
    fetchTechnicalNeeds();
    fetchUserProfile(); 
  }, [])

  const [formData, setFormData] = useState({
    fullName: "",
    title: "",
    company: "",
    companySize: "",
    industry: "",
    annualRevenue: "",
    techBudget: "",
    email: "",
    phone: "",
    linkedin: "",
    bio: "",
    decisionMakingRole: "",
    purchasingAuthority: "",
    currentTechStack: "",
    urgentNeeds: [],
    timeline: "",
    previousPurchases: "",
    challenges: "",
    goals: "",
    preferredCommunication: "",
    referenceWillingness: false,
    caseStudyParticipation: false,
    exclusiveDeals: true
  });

  const [showProfileDialog, setShowProfileDialog] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleUrgentNeed = async (id) => {
    if (selectedNeeds.includes(id)) {
      // Remove the need
      const res = await removeTechnologyNeed(id);
      if (res.success) {
        setSelectedNeeds(prev => prev.filter(needId => needId !== id));
      } else {
        alert("Failed to remove technology need");
      }
    } else {
      // Add the need
      const res = await addTechnologyNeed(id);
      if (res.success) {
        setSelectedNeeds(prev => [...prev, id]);
      } else {
        alert("Failed to add technology need");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (techstack.trim() === "" || businessChallenges.trim() === "" || technologyGoals.trim() === "" || linkedinUrl.trim() == "") {
      alert("Please fill out all fields.");
      return;
    }

    setIsSubmitting(true);
    let res = await updateUserProfile({
      technology_stack: techstack, 
      business_challenges: businessChallenges, 
      technology_goals: technologyGoals,
      purchasing_role: purchasingRole,
      purchasing_timeline: purchasingTimeline,
      linkedin_url: linkedinUrl,
      biography: biography,
    });

    let res2 = await saveExclusiveRequest();

    if (res.success) {
      setShowConfirmation(true);
    } else {
      alert("There was an error submitting your application. Please try again.");
    }
    setIsSubmitting(false);
  };

  const ProfilePreview = () => {
    // Mock data to show what a completed profile looks like
    const mockData = {
      fullName: formData.fullName || "Sarah Chen",
      title: formData.title || "VP of Engineering",
      company: formData.company || "TechFlow Solutions",
      industry: formData.industry || "Financial Technology",
      companySize: formData.companySize || "midsize",
      annualRevenue: formData.annualRevenue || "10m-100m",
      techBudget: formData.techBudget || "$250K - $500K annually",
      email: formData.email || "sarah.chen@techflow.com",
      phone: formData.phone || "+1 (555) 123-4567",
      linkedin: formData.linkedin || "https://linkedin.com/in/sarahchen-vp",
      bio: formData.bio || "Experienced technology executive with 12+ years leading engineering teams at high-growth fintech companies. Previously scaled engineering teams from 15 to 150+ at two successful Series B startups. Passionate about building scalable infrastructure, implementing DevOps best practices, and fostering inclusive engineering cultures. Currently focused on modernizing our tech stack and improving deployment velocity while maintaining security compliance in the financial sector.",
      decisionMakingRole: formData.decisionMakingRole || "final-decision",
      purchasingAuthority: formData.purchasingAuthority || "Up to $100K without board approval",
      currentTechStack: formData.currentTechStack || "AWS, Kubernetes, React, Node.js, PostgreSQL, Redis, Kafka, Terraform, Jenkins, Datadog, PagerDuty. Currently migrating from monolith to microservices architecture.",
      urgentNeeds: formData.urgentNeeds.length > 0 ? formData.urgentNeeds : ["Cloud Infrastructure", "DevOps Tools", "Cybersecurity", "Data Analytics"],
      timeline: formData.timeline || "short-term",
      previousPurchases: formData.previousPurchases || "Recently purchased Datadog ($50K/year), GitHub Enterprise ($25K/year), and AWS Reserved Instances ($120K/year). Previously evaluated but didn't purchase: New Relic, Splunk, CircleCI.",
      challenges: formData.challenges || "1) Scaling infrastructure to handle 10x traffic growth, 2) Reducing deployment time from 2 hours to under 30 minutes, 3) Improving system observability and incident response, 4) Ensuring SOC2 compliance while maintaining development velocity.",
      goals: formData.goals || "Implement container orchestration platform, establish proper CI/CD pipelines, enhance monitoring and alerting capabilities, improve disaster recovery processes, and reduce technical debt by 40%.",
      preferredCommunication: formData.preferredCommunication || "email",
      referenceWillingness: formData.referenceWillingness !== undefined ? formData.referenceWillingness : true,
      caseStudyParticipation: formData.caseStudyParticipation !== undefined ? formData.caseStudyParticipation : true,
      exclusiveDeals: formData.exclusiveDeals !== undefined ? formData.exclusiveDeals : true
    };

    return (
      <div className="space-y-6 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="text-center border-b pb-4">
          <div className="flex justify-center mb-4">
            <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center text-lg font-semibold text-gray-600">
              {mockData.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
          </div>
          <h2 className="text-2xl font-bold">{mockData.fullName}</h2>
          <p className="text-lg text-gray-600">{mockData.title}</p>
          <p className="text-sm text-gray-500">{mockData.company}</p>
          
        </div>

        {/* Bio Section */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Professional Bio</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{mockData.bio}</p>
        </div>

        {/* Company Profile */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Company Profile</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-gray-500" />
              <span className="text-sm"><strong>Industry:</strong> {mockData.industry}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="text-sm"><strong>Size:</strong> {mockData.companySize === "startup" ? "Startup (1-50)" : mockData.companySize === "midsize" ? "Mid-size (51-500)" : "Enterprise (500+)"}</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-500" />
              <span className="text-sm"><strong>Revenue:</strong> {mockData.annualRevenue === "under10m" ? "Under $10M" : mockData.annualRevenue === "10m-100m" ? "$10M - $100M" : "Over $100M"}</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-gray-500" />
              <span className="text-sm"><strong>Tech Budget:</strong> {mockData.techBudget}</span>
            </div>
          </div>
        </div>

        {/* Decision Making */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Decision Making Authority</h3>
          <div className="text-sm">
            <strong>Role:</strong> {mockData.decisionMakingRole === "final-decision" ? "Final Decision Maker" : mockData.decisionMakingRole === "strong-influence" ? "Strong Influence on Decisions" : "Recommender/Evaluator"}
          </div>
          <div className="text-sm">
            <strong>Purchasing Authority:</strong> {mockData.purchasingAuthority}
          </div>
        </div>

        {/* Technology Needs */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Technology Needs</h3>
          <div>
            <p className="text-sm font-medium mb-2">Urgent Needs:</p>
            <div className="flex flex-wrap gap-2">
              {mockData.urgentNeeds.map((need) => (
                <span key={need} className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-800">
                  {need}
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="text-sm">
              <strong>Timeline:</strong> {mockData.timeline === "immediate" ? "Immediate (0-30 days)" : mockData.timeline === "short-term" ? "Short-term (1-3 months)" : mockData.timeline === "medium-term" ? "Medium-term (3-6 months)" : "Long-term (6+ months)"}
            </span>
          </div>
        </div>

        {/* Current Technology Stack */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Current Technology Stack</h3>
          <p className="text-sm text-gray-600">{mockData.currentTechStack}</p>
        </div>

        {/* Previous Purchases */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Previous Technology Purchases</h3>
          <p className="text-sm text-gray-600">{mockData.previousPurchases}</p>
        </div>

        {/* Key Business Challenges */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Key Business Challenges</h3>
          <p className="text-sm text-gray-600">{mockData.challenges}</p>
        </div>

        {/* Technology Goals */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg border-b pb-2">Technology Goals (Next 12 Months)</h3>
          <p className="text-sm text-gray-600">{mockData.goals}</p>
        </div>


        {/* Buyer Quality Score */}
        <div className="space-y-3 bg-green-50 p-4 rounded-lg border-green-200 border">
          <h3 className="font-semibold text-lg text-green-800 border-b border-green-300 pb-2">Buyer Quality Score</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-green-700">Decision Authority:</span>
              <span className="ml-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-green-600 text-white">High</span>
            </div>
            <div>
              <span className="font-medium text-green-700">Budget Confirmed:</span>
              <span className="ml-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-green-600 text-white">Yes</span>
            </div>
            <div>
              <span className="font-medium text-green-700">Timeline:</span>
              <span className="ml-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-yellow-500 text-white">Short-term</span>
            </div>
            <div>
              <span className="font-medium text-green-700">Engagement Level:</span>
              <span className="ml-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-green-600 text-white">High</span>
            </div>
          </div>
          <div className="mt-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-green-700">Overall Score:</span>
              <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-green-200 rounded-full overflow-hidden">
                  <div className="w-4/5 h-full bg-green-600"></div>
                </div>
                <span className="text-sm font-bold text-green-800">8.5/10</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Enhanced Header Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center items-center gap-2 mb-6">
            <Crown className="h-10 w-10 text-purple-600" />
            <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Exclusive Buyers Circle
            </h1>
          </div>
          
          <p className="text-2xl text-gray-700 max-w-3xl mx-auto mb-6 leading-relaxed">
            Join an elite group of verified software buyers and unlock premium vendor relationships, 
            exclusive deals, and personalized technology recommendations.
          </p>

          {/* Accepted Member Banner */}
          {userProfile?.is_exclusive && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
              <div className="flex items-center gap-3">
                <Shield className="h-6 w-6 text-green-600" />
                <div className="text-left">
                  <h4 className="font-semibold text-green-800">Verified Exclusive Member</h4>
                  <p className="text-sm text-green-700">
                    Welcome to the Exclusive Buyers Circle. You have full access to premium features and exclusive vendor deals.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* How It Works */}
          <div className="bg-white rounded-xl p-6 mb-8 shadow-sm border">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">How It Works</h3>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-full p-2 mt-1">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-1">1. Apply & Get Verified</h4>
                  <p className="text-sm text-gray-600">Complete our detailed application and verification process to join the exclusive circle.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-full p-2 mt-1">
                  <Target className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-1">2. Get Matched</h4>
                  <p className="text-sm text-gray-600">Receive curated vendor recommendations based on your specific technology needs and budget.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-green-100 rounded-full p-2 mt-1">
                  <Clock className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-1">3. Access Premium Deals</h4>
                  <p className="text-sm text-gray-600">Enjoy exclusive pricing, priority support, and early access to new products.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Membership Requirements - Only show if not exclusive */}
          {!userProfile?.is_exclusive && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
              <h4 className="font-semibold text-amber-800 mb-2">Membership Requirements</h4>
              <p className="text-sm text-amber-700">
                This exclusive program is for verified technology decision-makers with purchasing authority at companies. Applications undergo a thorough vetting process.
              </p>
            </div>
          )}
        </div>


        {/* Application Form */}
        <div className="rounded-lg border bg-white text-gray-900 shadow-sm">
          <div className="flex flex-col space-y-1.5 p-6">
            <h3 className="text-2xl font-semibold leading-none tracking-tight flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-600" />
              Exclusive Membership Application
            </h3>
            <p className="text-sm text-gray-600">
              Provide detailed information to help us create your premium buyer profile and match you with the right vendors
            </p>
          </div>
          
          <div className="p-6 pt-0">
            <div className="space-y-6">


              {/* Decision Making */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Decision Making Authority</h3>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Role in Technology Purchasing</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="final-decision"
                        name="decisionMakingRole"
                        value="final-decision"
                        checked={purchasingRole === "final-decision"}
                        onChange={() => setPurchasingRole("final-decision")}
                        className="h-4 w-4 rounded-full border border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500"
                      />
                      <label htmlFor="final-decision" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Final Decision Maker</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="strong-influence"
                        name="decisionMakingRole"
                        value="strong-influence"
                        checked={purchasingRole === "strong-influence"}
                        onChange={() => setPurchasingRole("strong-influence")}
                        className="h-4 w-4 rounded-full border border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500"
                      />
                      <label htmlFor="strong-influence" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Strong Influence on Decisions</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="recommender"
                        name="decisionMakingRole"
                        value="recommender"
                        checked={purchasingRole === "recommender"}
                        onChange={() => setPurchasingRole("recommender")}
                        className="h-4 w-4 rounded-full border border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500"
                      />
                      <label htmlFor="recommender" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Recommender/Evaluator</label>
                    </div>
                  </div>
                </div>
              
              </div>

              {/* Tech Needs */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Technology Needs & Timeline</h3>
                
                <div className="space-y-3">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Current Urgent Technology Needs</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {technicalNeeds.map((need) => (
                      <div key={need.technology_needs_id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`need-${need.technology_needs_id}`}
                          checked={selectedNeeds.includes(need.technology_needs_id)}
                          onChange={() => toggleUrgentNeed(need.technology_needs_id)}
                          className="h-4 w-4 rounded border border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500"
                        />
                        <label 
                          htmlFor={`need-${need.technology_needs_id}`}
                          className="text-sm cursor-pointer"
                        >
                          {need.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Purchase Timeline</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="immediate"
                        name="timeline"
                        value="immediate"
                        checked={purchasingTimeline === "immediate"}
                        onChange={() => setPurchasingTimeline("immediate")}
                        className="h-4 w-4 rounded-full border border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500"
                      />
                      <label htmlFor="immediate" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Immediate (0-30 days)</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="short-term"
                        name="timeline"
                        value="short-term"
                        checked={purchasingTimeline === "short-term"}
                        onChange={() => setPurchasingTimeline("short-term")}
                        className="h-4 w-4 rounded-full border border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500"
                      />
                      <label htmlFor="short-term" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Short-term (1-3 months)</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="medium-term"
                        name="timeline"
                        value="medium-term"
                        checked={purchasingTimeline === "medium-term"}
                        onChange={() => setPurchasingTimeline("medium-term")}
                        className="h-4 w-4 rounded-full border border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500"
                      />
                      <label htmlFor="medium-term" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Medium-term (3-6 months)</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="long-term"
                        name="timeline"
                        value="long-term"
                        checked={purchasingTimeline === "long-term"}
                        onChange={() => setPurchasingTimeline("long-term")}
                        className="h-4 w-4 rounded-full border border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500"
                      />
                      <label htmlFor="long-term" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Long-term (6+ months)</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Additional Information</h3>
                
                <div className="space-y-2">
                  <label htmlFor="biography" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Professional Biography</label>
                  <textarea
                    id="biography"
                    name="biography"
                    value={biography}
                    onChange={(e) => setBiography(e.target.value)}
                    placeholder="Tell us about your professional background and experience"
                    rows={4}
                    className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="currentTechStack" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Current Technology Stack</label>
                  <textarea
                    id="currentTechStack"
                    name="currentTechStack"
                    value={techstack}
                    onChange={(e) => setTechstack(e.target.value)}
                    placeholder="Describe your current technology infrastructure and tools"
                    rows={3}
                    className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="challenges" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Key Business Challenges</label>
                  <textarea
                    id="challenges"
                    name="challenges"
                    value={businessChallenges}
                    onChange={(e) => setBusinessChallenges(e.target.value)}
                    placeholder="What are your biggest business challenges that technology could help solve?"
                    rows={3}
                    className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="goals" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Technology Goals for Next 12 Months</label>
                  <textarea
                    id="goals"
                    name="goals"
                    value={technologyGoals}
                    onChange={(e) => setTechnologyGoals(e.target.value)}
                    placeholder="What technology objectives are you looking to achieve?"
                    rows={3}
                    className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                </div>
              </div>

              {/* Contact & Preferences */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Contact Information</h3>
                

                <div className="space-y-2">
                  <label htmlFor="linkedin" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">LinkedIn Profile</label>
                  <input
                    id="linkedin"
                    name="linkedin"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center">
                {/* <button
                  type="button"
                  onClick={() => setShowProfileDialog(true)}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-gray-300 bg-white hover:bg-gray-50 hover:text-gray-900 h-10 px-4 py-2"
                >
                  <Eye className="h-4 w-4" />
                  Preview Profile
                </button> */}

                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 h-10 px-8 py-3"
                >
                  {userProfile?.is_exclusive ? 'Update Profile' : 'Apply for Exclusive Membership'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Preview Modal */}
        {showProfileDialog && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex flex-col space-y-1.5 p-6 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Profile Preview</h2>
                    <p className="text-sm text-gray-600">
                      This is how your profile will appear to potential vendors
                    </p>
                  </div>
                  <button
                    onClick={() => setShowProfileDialog(false)}
                    className="rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:pointer-events-none"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-6">
                <ProfilePreview />
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {showConfirmation && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {userProfile?.is_exclusive ? 'Profile Updated!' : 'Application Submitted!'}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {userProfile?.is_exclusive 
                    ? 'Your profile has been successfully updated. Changes will be reflected immediately.'
                    : 'Thank you for applying to the Exclusive Buyers Circle. Our team will review your profile within 24-48 hours and get back to you shortly.'}
                </p>
              </div>
              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    setShowConfirmation(false);
                  }}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-green-500"
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExclusiveBuyerForm;